import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ResizableSwappableCard } from "@/components/admin/dashboard/ResizableSwappableCard";
import { D3LineChart, D3ResponsiveContainer } from "../charts/D3ChartUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

interface ContractsTimelineChartProps {
  contractsTimeline: any[];
  rearrangingEnabled: boolean;
  onRemove: (id: string) => void;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export function ContractsTimelineChart({
  contractsTimeline,
  rearrangingEnabled,
  onRemove,
  size,
  onResize
}: ContractsTimelineChartProps) {
  const { t } = useEnhancedTranslation();
  const [formattedData, setFormattedData] = useState<any[]>([]);
  
  useEffect(() => {
    if (contractsTimeline && contractsTimeline.length > 0) {
      // Process and format the data for D3
      const processed = contractsTimeline.map(item => ({
        name: item.date || '',
        value: typeof item.count === 'number' ? item.count : 0
      }));
      console.log("Processed contracts timeline data:", processed);
      setFormattedData(processed);
    } else {
      // Provide sample data if no data is available
      const sampleData = [
        { name: 'Jan 2025', value: 2 },
        { name: 'Feb 2025', value: 5 },
        { name: 'Mar 2025', value: 3 },
        { name: 'Apr 2025', value: 7 },
        { name: 'May 2025', value: 4 }
      ];
      setFormattedData(sampleData);
      console.log("Using sample data for timeline chart");
    }
  }, [contractsTimeline]);

  const valueFormatter = (value: number) => {
    return t("dashboard.widgets.contractCount", { value: value });
  };

  return (
    <ResizableSwappableCard
      id="contracts-timeline"
      size={size}
      showHandle={rearrangingEnabled}
      showResizeControl={rearrangingEnabled}
      onRemove={rearrangingEnabled ? onRemove : undefined}
      onResize={rearrangingEnabled && onResize ? (newSize) => onResize("contracts-timeline", newSize) : undefined}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('dashboard.widgets.contractsTimeline')}</CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('dashboard.widgets.hoverInfo')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <D3ResponsiveContainer width="100%" height="100%">
            <D3LineChart
              data={formattedData}
              color="#8884d8"
              valueFormatter={valueFormatter}
              margin={{ top: 20, right: 30, bottom: 50, left: 40 }}
              areaFill={true}
            />
          </D3ResponsiveContainer>
        </div>
      </CardContent>
    </ResizableSwappableCard>
  );
}
